import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { api } from '../servizi/api.ts'
import { useCatalogo, useSessione } from '../servizi/sessione.tsx'
import { Bottone, Campo, Modale, Paginazione, Riquadro, Scelta, Tabella } from '../componenti/base.tsx'
import { Attesa, Avviso, Intestazione, Vuoto } from '../componenti/Pezzi.tsx'
import { LIMITE, conteggio, usaElenco } from '../componenti/elenco.tsx'
import { IconaPiu } from '../componenti/Icone.tsx'
import { ModuloEnte, SceltaComune, luogoEnte, type ComuneScelto } from './Ente.tsx'
import { opzioniSedi } from '../servizi/sedi.ts'
import { ProposteDoppioniEnti, UnisciGruppo, type Gruppo } from './enti/UnisciEnti.tsx'

/**
 * Elenco degli enti (D-23) con i filtri per profilo ente, comune, sede CIDIS, testo
 * e stato. I filtri stanno nell'indirizzo della pagina: un elenco filtrato si puo' condividere.
 *
 * «Unisci i doppioni» (475) propone gli enti registrati piu' volte; i nomi degli enti uniti restano
 * sotto quello tenuto («Anche: …») e la ricerca li trova.
 */

const CHIAVI_FILTRO = ['cerca', 'categoria', 'comune', 'sede', 'attivo'] as const
type ChiaveFiltro = (typeof CHIAVI_FILTRO)[number]

type RigaEnte = {
  id: string; sigla: string | null; nome: string
  categoria_ente: string | null; categoria_nome: string | null; profilo_nome: string | null
  paese: string | null; paese_nome: string | null; comune_nome: string | null; provincia_sigla: string | null
  citta: string | null; email: string | null; telefono: string | null; attivo: boolean
  // N-2: valorizzati sugli enti che erano sedi di CIDIS stessa e sono diventati sedi.
  sostituito_da_sede: string | null; sostituito_da_sede_nome: string | null
  sedi: { id: string; nome: string }[]
  /** Nomi degli enti uniti a questo come doppioni (475). */
  altri_nomi: string[]
}

export function Enti() {
  const { puo, sedi } = useSessione()
  const categorie = useCatalogo('categoria_ente')
  const naviga = useNavigate()
  const [parametri, setParametri] = useSearchParams()
  const [creazione, setCreazione] = useState(false)
  const [testo, setTesto] = useState(() => parametri.get('cerca') ?? '')
  const [comune, setComune] = useState<ComuneScelto | null>(null)
  const [proposte, setProposte] = useState(false)
  const [gruppo, setGruppo] = useState<Gruppo | null>(null)
  const [esito, setEsito] = useState<string | null>(null)

  const filtro = (chiave: ChiaveFiltro) => parametri.get(chiave) ?? ''
  const aggiorna = useCallback((chiave: ChiaveFiltro, valore: string) => {
    setParametri((attuali) => {
      const nuovi = new URLSearchParams(attuali)
      if (valore) nuovi.set(chiave, valore); else nuovi.delete(chiave)
      return nuovi
    }, { replace: true })
  }, [setParametri])

  // Il testo passa nell'indirizzo dopo una pausa nella digitazione: ogni lettura dell'elenco resta
  // nel registro eventi, e una per tasto non serve a nessuno.
  const cercaInUso = parametri.get('cerca') ?? ''
  useEffect(() => {
    const termine = testo.trim()
    if (termine === cercaInUso) return
    const attesa = setTimeout(() => aggiorna('cerca', termine), 350)
    return () => clearTimeout(attesa)
  }, [testo, cercaInUso, aggiorna])

  // Il filtro per comune arriva dall'indirizzo come codice ISTAT: il nome si recupera una volta.
  const comuneFiltrato = parametri.get('comune')
  useEffect(() => {
    if (!comuneFiltrato) { setComune(null); return }
    if (comune?.codice_istat === comuneFiltrato) return
    const ripiego = { codice_istat: comuneFiltrato, nome: comuneFiltrato, provincia_sigla: null }
    api.get<ComuneScelto[]>(`/riferimenti/comuni?cerca=${encodeURIComponent(comuneFiltrato)}&limite=5`)
      .then((r) => setComune(r.find((c) => c.codice_istat === comuneFiltrato) ?? ripiego))
      .catch(() => setComune(ripiego))
  }, [comuneFiltrato]) // eslint-disable-line react-hooks/exhaustive-deps

  const query = useMemo(() => {
    const q = new URLSearchParams()
    for (const chiave of CHIAVI_FILTRO) {
      const valore = parametri.get(chiave)
      if (valore) q.set(chiave, valore)
    }
    // Gli enti diventati sedi CIDIS (N-2) stanno fuori dall'anagrafica: si vedono soltanto
    // guardando i disattivati, ed e' li' che la riga dice su quale sede e' finito il loro storico.
    if (parametri.get('attivo') === 'false') q.set('sostituiti', 'true')
    return q.toString()
  }, [parametri])
  const { dati, errore, finestra, ricarica } = usaElenco<RigaEnte>(`/enti${query ? `?${query}` : ''}`)
  const conFiltri = CHIAVI_FILTRO.some((chiave) => parametri.get(chiave))

  function azzera() {
    setTesto('')
    setComune(null)
    setParametri(new URLSearchParams(), { replace: true })
  }

  return (
    <>
      <Intestazione
        titolo="Enti"
        sottotitolo={conteggio(finestra.totale, 'ente', 'enti')}
        azioni={puo('ente.gestisci')
          ? <>
              <Bottone variante="secondario" onClick={() => { setEsito(null); setProposte(true) }}>Unisci i doppioni</Bottone>
              <Bottone variante="primario" icona={<IconaPiu />} onClick={() => setCreazione(true)}>Nuovo ente</Bottone>
            </>
          : undefined}
      />
      {esito && <Avviso tipo="successo">{esito}</Avviso>}

      <Riquadro titolo="Filtri" azioni={conFiltri
        ? <Bottone variante="discreto" dimensione="piccolo" onClick={azzera}>Azzera i filtri</Bottone>
        : undefined}>
        <div className="filtri-report">
          <Campo etichetta="Cerca" tipo="search" valore={testo} cambia={setTesto} segnaposto="Nome o acronimo" />
          {/* Un filtro solo (Osservazioni 3, punto 8): le voci sono quelle di ref.categoria_ente. */}
          <Scelta etichetta="Profilo ente" vuota="Tutti" valore={filtro('categoria')}
                  cambia={(v) => aggiorna('categoria', v)}
                  opzioni={categorie.voci.map((c) => ({ valore: c.codice, etichetta: c.nome }))} />
          <Scelta etichetta="Sede CIDIS" vuota="Tutte" valore={filtro('sede')} cambia={(v) => aggiorna('sede', v)}
                  opzioni={opzioniSedi(sedi)} />
          <Scelta etichetta="Stato" valore={filtro('attivo') === 'false' ? 'false' : 'true'}
                  cambia={(v) => aggiorna('attivo', v === 'false' ? 'false' : '')}
                  opzioni={[{ valore: 'true', etichetta: 'Attivi' }, { valore: 'false', etichetta: 'Disattivati' }]} />
          <SceltaComune etichetta="Comune" vuota="Tutti i comuni" valore={comune}
                        cambia={(c) => { setComune(c); aggiorna('comune', c?.codice_istat ?? '') }} />
        </div>
      </Riquadro>

      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {!errore && !dati && <Attesa />}
      {dati && (
        <Riquadro denso>
          <Tabella intestazioni={['Ente', 'Profilo ente', 'Luogo', 'Sedi CIDIS', 'Contatti']}
                   vuoto={<Vuoto testo={conFiltri ? 'Nessun ente con questi filtri fra quelli che puoi vedere.' : 'Nessun ente registrato.'} />}>
            {dati.map((e) => (
              <tr key={e.id}>
                <td data-etichetta="Ente">
                  <span className="dato-testo">
                    <Link to={`/enti/${e.id}`} className="voce-principale">
                      {e.sigla ? `${e.sigla} — ` : ''}{e.nome}
                    </Link>
                    {e.sostituito_da_sede
                      ? <span className="piccolo tenue">
                          Ora sede CIDIS:{' '}
                          <Link to={`/sedi/${e.sostituito_da_sede}`}>{e.sostituito_da_sede_nome}</Link>
                        </span>
                      : null}
                    {e.altri_nomi?.length > 0 && <span className="piccolo tenue">Anche: {e.altri_nomi.join(', ')}</span>}
                  </span>
                </td>
                <td data-etichetta="Profilo ente">
                  {e.categoria_nome ?? <span className="tenue">non indicato</span>}
                </td>
                <td data-etichetta="Luogo">{luogoEnte(e) ?? <span className="tenue">—</span>}</td>
                <td data-etichetta="Sedi CIDIS">
                  {e.sedi.length ? e.sedi.map((s) => s.nome).join(', ') : <span className="tenue">—</span>}
                </td>
                <td data-etichetta="Contatti" className="piccolo tenue">
                  {[e.email, e.telefono].filter(Boolean).join(' · ') || '—'}
                </td>
              </tr>
            ))}
          </Tabella>
          <Paginazione salta={finestra.salta} limite={LIMITE} totale={finestra.totale}
                       ricevute={dati.length} cambia={finestra.cambia} />
        </Riquadro>
      )}

      {proposte && (
        <ProposteDoppioniEnti chiudi={() => setProposte(false)}
                              rivedi={(g) => { setProposte(false); setGruppo(g) }} />
      )}
      {gruppo && (
        <UnisciGruppo gruppo={gruppo} chiudi={() => { setGruppo(null); ricarica() }}
                      fatto={(testo) => { setGruppo(null); setEsito(testo); ricarica() }} />
      )}
      {creazione && (
        <Modale titolo="Nuovo ente" larga chiudi={() => setCreazione(false)}>
          <ModuloEnte annulla={() => setCreazione(false)}
                      salvato={(id) => { setCreazione(false); naviga(`/enti/${id}`) }} />
        </Modale>
      )}
    </>
  )
}
